/**
 * components/Shell.tsx — the application chrome, built ONCE and shared by every view.
 *
 * Per-page navigation is the most visible form of drift there is, so no screen draws its own: a view
 * receives the frame and fills the main region. The frame transforms at `--bp-md` — a fixed bottom tab bar
 * below, a persistent rail above — and the transform is one component with two stylesheets rather than two
 * components, because two components drift the moment one gains an item.
 *
 * The tab bar carries an icon AND a visible label. Below `--bp-md` an icon-only rail with hover tooltips is
 * forbidden: touch has no hover, so the label would be unreachable and the navigation would be a row of
 * unlabelled glyphs.
 *
 * **On the rail the label is NOT text — it is a tooltip, and that is not a preference.** `02-patterns.md`
 * §1.4: *"The rail is icon-only; a labelled sidebar is no longer a permitted alternative"*, and the name
 * *"MUST NOT be set as text in the rail"*. It is carried instead by `aria-label` here and by a tooltip in
 * `styles.css` that opens on hover **and on focus**. This file argued the opposite until 7 August 2026 —
 * *"the labels stay too, desktop has the width"* — which was true against design-system v5.2.1, where an
 * icon-only rail was one permitted option; v5.4.0 withdrew the alternative and nobody re-read the comment.
 * `design-deviations.md` had it filed as departure-with-no-reason for a day before it was fixed.
 *
 * ONE `<span>` serves both bands: visible label below `--bp-md`, tooltip above it. `aria-label` repeats it
 * verbatim rather than replacing it, so the accessible name still contains the visible text where there
 * is one (WCAG 2.5.3), and still exists where there is not.
 *
 * **The business-unit switcher lives here, in the top bar, and that placement is a safety property rather
 * than a layout preference.** Every request this client makes names its scope (`lib/api.ts`), and the
 * server refuses a mutation whose named scope disagrees with the Decision it resolved — the multi-tab
 * guard. A switcher buried inside one screen would let a user believe the scope is per-screen; in the top
 * bar it is visibly the state of the whole window, which is what it is. The current scope is also named in
 * the bar as text, so a screenshot of a wrong-tenant row shows which tenant the window was on.
 *
 * **The app mark is a link, and it has no wordmark beside it.** Both are required by the design system
 * (`02-patterns.md` §1.3) and both were wrong here until the mark was drawn: the bar carried an inert
 * `<span>` holding the letter `t`, with the word "Ticketless" set in text next to it. The link matters
 * because it is the affordance a lost user reaches for and it must work on the deepest page; the missing
 * wordmark matters because the application's name beside its own mark is a second copy of the one fact
 * nobody in the window is in doubt about. The name is not lost — it is the link's accessible name.
 *
 * **Identity and Sign out are in the account menu at the top RIGHT, not in the bar.** §1.3 fixes the
 * anatomy — mark, context switcher, flexible space, global actions, account menu — and the spacer below
 * is what pins everything after it to the right gutter. What stays in the bar is the mark and the
 * business-unit switcher; the switcher does not follow the rest into the panel, because a business unit
 * is the tenant the whole window is on rather than a preference about it. **The theme choice is no
 * longer passed through here at all**: `03-components.md` §15.3 forbids a form control in the account
 * menu by name, so the three-way control left the panel in the audit of 2026-08-07 and `App` now hands
 * the same state to `pages/Settings.tsx` alone.
 *
 * **`.shell__chrome` wraps the bar, and it is not a spare div.** `02-patterns.md` §1.3 requires the bar's
 * height to be a **declared constant** that is not derived from padding around its children, and requires
 * `env(safe-area-inset-top)` in installed (PWA) mode to be absorbed **above** the bar rather than as the
 * bar's own padding — an inset added to the bar stacks onto the declared height and makes the one
 * dimension that is meant to be constant vary by device. That was `design-deviations.md` **D7**, recorded
 * with "not a one-liner: the inset cannot simply move to `.shell`, because the bar is `position: sticky`
 * and would then stick underneath the notch". This wrapper is the missing element: it takes the sticky
 * position, the stacking rung and the inset, and the bar inside it is a plain row of a declared height.
 * A sticky element can only stick within its own parent's box, so the sticky **must** be the wrapper and
 * not the `<header>` — a `position: sticky` bar inside a wrapper exactly its own height scrolls straight
 * away, which looks like the bar simply not sticking any more.
 *
 * **One defect closed in passing.** `.topbar__identity` carried two different facts — the signed-in
 * person and the single-scope name — and was `display:none` below `--bp-md`, which made this file's own
 * claim above ("the current scope is also named in the bar as text") false on every phone. The class is
 * now `.topbar__scope`, it names the scope and only the scope, and it is visible at every width.
 */
import type { ReactNode } from "react";
import type { Me } from "../lib/api";
import { canReachAdmin } from "../lib/admin";
import { Mark } from "../brand/Mark";
import { AccountMenu } from "./AccountMenu";

export interface NavItem {
  readonly key: string;
  readonly label: string;
  /** The hash route this item leads to, e.g. `#/jobs`. */
  readonly href: string;
  readonly icon: ReactNode;
}

export interface ShellProps {
  readonly me: Me;
  readonly nav: readonly NavItem[];
  readonly activeKey: string;
  readonly activeBu: string | null;
  readonly onSelectBu: (buId: string) => void;
  readonly onSignOut: () => void;
  readonly children: ReactNode;
}

/** Inline SVG, sized by the stylesheet and coloured by `currentColor` — no icon dependency, no palette. */
function icon(path: string): ReactNode {
  return (
    <svg
      className="nav-item__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={path} />
    </svg>
  );
}

export const ICONS = {
  jobs: icon("M4 6h16M4 12h16M4 18h10"),
  runs: icon("M12 8v4l3 2M21 12a9 9 0 1 1-9-9"),
  people: icon("M16 19v-1a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v1M9.5 7.5a3 3 0 1 0 0 .01M17 11h4"),
  /** A key: what a group carries, rather than who is in it. */
  access: icon("M15 7a4 4 0 1 0 0 .01M12.5 9.5L4 18v2h3v-2h2v-2h2z"),
  /** Stacked slabs: separate tenancies, side by side and not touching. */
  businessUnits: icon("M4 20V9l6-4 6 4v11M4 20h16M14 20v-6h6v6M7 12h3M7 16h3"),
  audit: icon("M8 4h9l3 3v13H8zM4 8v12h9M11 11h6M11 15h6"),
  /** A shield: the administration console. Distinguishable from `jobs`'s three lines at 20px. */
  admin: icon("M12 3l7 3v5.5c0 4.2-2.9 6.9-7 8.5-4.1-1.6-7-4.3-7-8.5V6z"),
  /**
   * A target: an objective, and the key results closing on it.
   *
   * Three concentric rings, which at 20px is a filled-looking bullseye and is the point — it shares no
   * silhouette with `jobs` (three horizontal lines) or `admin` (a shield), the two it sits between on the
   * rail. Drawn as one path like its siblings, so the shared `icon()` construction still applies.
   */
  goals: icon(
    "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18M12 16.5a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9M12 13.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3",
  ),
} as const;

/**
 * The primary navigation, for a given subject — **stated once, and here rather than in `App.tsx`**.
 *
 * Three entries at most, where there were five. The four administrative screens were flat peers of the work
 * screen, so an ordinary user's whole navigation was one item and an administrator's was five things at
 * one level. They are now one destination whose contents dilate with the caller's grants. There is
 * deliberately no superuser area beside it: enumerating destinations a caller could never hold is
 * forbidden by `02-patterns.md` §2.4 and `06-modules.md` §1.7 alike, and a section visible only to a
 * superuser would be a menu of one, since every administrative verb here is already superuser-only.
 *
 * ⚠️ **Since 7 August 2026 the two work entries are gated too, and the gate is READ rather than computed.**
 * `me.modules` is the server's own answer, per module, from `permissions/modules.ts` — the only copy of the
 * rule. Do not write `me.roles.includes("job_author") || me.roles.includes("bu_admin")` here, however
 * obvious it looks: that is a second decision path, and the more permissive of two always becomes the
 * effective policy. The failure it produces is not a hidden button but a drawn one the route then refuses,
 * or — the direction that actually hurts — an entry hidden from somebody every route in the module answers.
 * All three entries here are now drawn from an answer computed elsewhere, which is the property worth
 * keeping.
 *
 * The console is reachable from this rail AND from the account menu, which is two controls for one
 * destination and is the one call here that could reasonably go the other way. It is taken because this
 * rail is the application's ONLY navigation and carried all four administrative destinations until today:
 * removing them from it entirely would be a larger change than was asked for, and would put the console
 * behind a menu on a phone. Both gates call `canReachAdmin`, so the two cannot diverge.
 *
 * ⚠️ **It is a function, and exported, because a copy of it in a test asserts the copy.** Until 6 August
 * 2026 this expression lived inline in `App.tsx` and `Shell.test.tsx` rebuilt it from a transcription —
 * so `canReachAdmin(me) || true` in `App.tsx` left the whole suite green, on the one claim the change was
 * named after. One definition, two callers, and `App.mount.test.tsx` asserts what `App` actually renders.
 */
export function primaryNav(me: Me): readonly NavItem[] {
  return [
    // The label is "Automations" and the key/href stay "jobs": the key is matched against `App.tsx`'s
    // computed `activeKey` and the href is a bookmarkable address, so renaming either would cost a legacy
    // alias and buy nothing. What was renamed on 7 August 2026 is the SECTION, not the entity inside it —
    // the things listed here are still "updates", because that is what they publish.
    ...(me.modules.automations
      ? [{ key: "jobs", label: "Automations", href: "#/jobs", icon: ICONS.jobs } as const]
      : []),
    // Gated since 7 August 2026, where it was ungated before. The old note here said `GET /api/goals`
    // needed no scope role, so every member of a unit could read which objectives it tracks; that is no
    // longer true — `moduleScopeOf(request, "goals")` runs before the read. The entry and the route now
    // say the same thing, which is the whole point: an entry drawn for a list the server refuses is worse
    // than no entry at all.
    ...(me.modules.goals
      ? [{ key: "goals", label: "Goals", href: "#/goals", icon: ICONS.goals } as const]
      : []),
    ...(canReachAdmin(me)
      ? [{ key: "admin", label: "Administration", href: "#/admin", icon: ICONS.admin } as const]
      : []),
  ];
}

export function Shell(props: ShellProps): JSX.Element {
  const { me, nav, activeKey } = props;
  const scopes = me.accessibleScopes;
  const currentScope = scopes.find((scope) => scope.id === props.activeBu);

  const items = nav.map((item) => (
    <a
      className="nav-item"
      key={item.key}
      href={item.href}
      aria-label={item.label}
      aria-current={item.key === activeKey ? "page" : undefined}
    >
      {item.icon}
      <span className="nav-item__label">{item.label}</span>
    </a>
  ));

  return (
    <div className="shell">
      {/*
        The first tab stop, and the only way a keyboard user skips the navigation on every page
        (`02-patterns.md` §4: "Skip-to-content MUST be the first focusable element on every page").

        ⚠️ **It must not be allowed to navigate, and that became load-bearing on 7 August 2026.** The
        href stays `#main` — it is what makes this a link to a place rather than a button that does
        something — but activating it is handled here instead of by the browser, because THIS
        APPLICATION ROUTES ON THE HASH. Letting the default through sets `window.location.hash` to
        `#main`, which `parseRoute` then has to have an opinion about; until that day it quietly
        resolved to the job list, and the moment the not-found state landed for `05-client.md` §5.3 it
        resolved to *that* — so the one control that exists to help assistive-technology users reach
        the content would have replaced the content with "that address does not match anything here",
        on every screen, with the address bar left at `#main` so a reload reproduced it.

        `focus()` on `<main tabIndex={-1}>` scrolls it into view and moves the caret there, which is
        the whole of what the native behaviour was for. The hash is left alone.
      */}
      <a
        className="skip-link"
        href="#main"
        onClick={(event) => {
          event.preventDefault();
          document.getElementById("main")?.focus();
        }}
      >
        Skip to content
      </a>

      {/* The wrapper, not the bar, is what sticks and what absorbs the notch — see the docblock. The bar
          is the row of controls and nothing else, so its height can be a constant §1.3 can rely on. */}
      <div className="shell__chrome">
        <header className="topbar">
          {/* `#/` and not `/`: routing is a hash, so a root-relative href would reload the document to
              arrive at the same view. `parseRoute("")` is `home` — a route of its own since 7 August
              2026, resolved by `landingRouteFor` to a destination this subject can actually reach,
              because the job list it used to mean is now a module not everybody holds. The affordance a
              lost user reaches for must not land them on a refusal. */}
          <a className="topbar__brand" href="#/" aria-label="Ticketless — automations">
            <Mark className="topbar__brand-mark" />
          </a>

          <span className="topbar__spacer" />

          <div className="topbar__group">
            {/* One scope: named, not offered. A select with a single option invites a choice that isn't one. */}
            {scopes.length > 1 ? (
              <>
                <label className="visually-hidden" htmlFor="bu-switcher">
                  Business unit
                </label>
                <select
                  id="bu-switcher"
                  className="select"
                  value={props.activeBu ?? ""}
                  onChange={(event) => props.onSelectBu(event.target.value)}
                >
                  {props.activeBu === null ? (
                    <option value="" disabled>
                      Choose a business unit
                    </option>
                  ) : null}
                  {scopes.map((scope) => (
                    <option key={scope.id} value={scope.id}>
                      {scope.displayName}
                    </option>
                  ))}
                </select>
              </>
            ) : (
              <span className="topbar__scope">{currentScope?.displayName ?? "No business unit"}</span>
            )}

            {/* §15.1: last in the bar, at every width. It is not a global action and is never shed into
                a mobile overflow — the trailing edge of the bar is where a user has learned to find
                themselves, and they have learned it for the product rather than for the viewport. */}
            <AccountMenu
              me={me}
              activeBu={props.activeBu}
              scopes={scopes}
              onSignOut={props.onSignOut}
            />
          </div>
        </header>
      </div>

      <div className="shell__body">
        {/* Desktop: a persistent rail. Hidden below --bp-md, where the tab bar below is the navigation. */}
        <nav className="nav-rail" aria-label="Primary">
          {items}
        </nav>

        <main className="shell__main" id="main" tabIndex={-1}>
          {props.children}
        </main>
      </div>

      {/* Mobile: the same items, as a fixed bottom tab bar with visible labels. */}
      <nav className="nav-tabs" aria-label="Primary">
        {items}
      </nav>
    </div>
  );
}
